/**
 * useImpersonationState — BOLT-025.
 *
 * Decodes the current access token and exposes impersonation session metadata.
 * Returns `isImpersonating: false` when the token has no `impersonator_id` claim.
 *
 * Reads `targetEmail` from the auth store (set at impersonation start).
 * On a fresh tab bootstrap the email may be null; callers render a fallback.
 */
import { useAuthStore } from '@/store/auth.js';
import { decodeJwtPayload } from '@/auth/impersonation.js';

interface ImpersonationClaims {
  sub: string;
  impersonator_id?: string;
  impersonator_session_id?: string;
  exp?: number;
}

export interface ImpersonationState {
  isImpersonating: boolean;
  targetEmail: string | null;
  sessionId: string | null;
  expiresAt: Date | null;
}

export function useImpersonationState(): ImpersonationState {
  const accessToken = useAuthStore((s) => s.accessToken);
  const targetEmail = useAuthStore((s) => s.targetEmail);

  const none: ImpersonationState = {
    isImpersonating: false,
    targetEmail: null,
    sessionId: null,
    expiresAt: null,
  };

  if (!accessToken) return none;

  const claims = decodeJwtPayload<ImpersonationClaims>(accessToken);
  if (!claims?.impersonator_id) return none;

  return {
    isImpersonating: true,
    targetEmail,
    sessionId: claims.impersonator_session_id ?? null,
    expiresAt: claims.exp !== null && claims.exp !== undefined ? new Date(claims.exp * 1000) : null,
  };
}
